import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { Chart } from "chart.js";
import { TranslateService } from "@ngx-translate/core";
import { Helper } from "app/shared/helper";

@Component({
  selector: "app-electricity-production-chart",
  templateUrl: "./electricity-production-chart.component.html",
  styleUrls: ["./electricity-production-chart.component.css"]
})
export class ElectricityProductionChartComponent implements OnInit {
  @ViewChild("chartElectricityProduction") canvas;

  @Input() chartID;
  @Input() chartData;
  @Input() unit;
  @Input() totalsInTooltip;
  @Input() valueType;

  private chart: Chart;
  private yAxes;
  private leftYAxisLabel;
  private rightYAxisLabel;
  private options;
  private dataUnit;
  private nTicksLeftYAxis: number = 1; // useful to align axes, use the auto value from ChartJS


  constructor(
    private helper: Helper,
    private translateService: TranslateService
  ) {}

  ngOnInit() {}

  ngOnChanges() {
    // create or update chart only when data is provided
    if (this.chartData !== undefined) {
      this.updateChartData();
      this.createChart();
    }
  }

  updateChartData() {
    // updates the recieved chart data with proper translations and units
    this.updatechartDataWithTranslation();
    this.updatechartDataForDesiredUnit();
  }

  updatechartDataWithTranslation() {
    // updates the chart datasets labels with the string provided by the translate service
    this.chartData.datasets.forEach((dataset, index: number): void => {
      let translatedLabel = this.translateService.instant(
        "DATA." + dataset.label
      );
      this.chartData.datasets[index].label = translatedLabel;
    });
  }

  updatechartDataForDesiredUnit() {
    // updates the datasets data and units to match the unit defined in the config file
    this.chartData.datasets.forEach((dataset, datasetIndex: number): void => {
      // get the conversion factor
      let currentUnit = dataset.unit[0].toLowerCase();
      let currentUnitInfo = this.helper.getUnitInfo(currentUnit);
      let desiredUnit = this.unit[dataset.physical_quantity];
      let conversionFactor = currentUnitInfo.convert_factor_from[desiredUnit];

      // convert each data value and unit
      dataset.data.forEach((dataValue, valueIndex: number): void => {
        let convertedValue = dataValue / conversionFactor;
        this.chartData.datasets[datasetIndex].data[valueIndex] = convertedValue;
        this.chartData.datasets[datasetIndex].unit[valueIndex] = desiredUnit;
        this.chartData.datasets[datasetIndex].unit[valueIndex] = desiredUnit;
      });
    });
  }

  createChart() {
    const ctx = this.canvas.nativeElement.getContext("2d");
    this.resetChart();
    this.setupYAxes();
    this.setupOptions();
    // this.options['spanGaps'] = true;
    this.chart = new Chart(ctx, {
      type: "bar",
      data: this.chartData,
      options: this.options
    });
    // this.alignYAxes();
  }

  setupYAxes() {
    this.getYAxesLabels();    
    this.yAxes = [
      {
        id: "left",
        position: "left",
        type: "linear",
        scaleLabel: {
          display: true,
          labelString: this.leftYAxisLabel,
          fontColor: "black",
          fontSize: 14
        },
        ticks: {
          beginAtZero: true,
          callback: this.yAxisTicksCallback
        },
        afterBuildTicks: (axis, ticks) => {
          // update the number of ticks from the main axis
          this.nTicksLeftYAxis = ticks.length - 1; // don't count start ticks
        }
      },
      {
        id: "right",
        position: "right",
        type: "linear",
        scaleLabel: {
          display: true,
          labelString: this.rightYAxisLabel,
          fontColor: "black",
          fontSize: 14
        },
        ticks: {
          beginAtZero: true,
          callback: this.yAxisTicksCallback
        },
        beforeBuildTicks: axis => {
          // configure step size to align the right axis to the left
          axis.chart.options.scales.yAxes[1].ticks.stepSize =
            axis.max / this.nTicksLeftYAxis;
        }
      }
      
    ];
  }

  getYAxesLabels() {
    // axis label is a combination of the axis label name and the axis unit
    let name; // read from the translate service
    let unit; // read from the translate service
    let unitInfo; // helps to use the translate service

    // energy axis
    name = this.translateService.instant(
      "DASHBOARD.CHARTS." + this.chartID + ".LEFT_Y_LABEL_NAME"
    );
    if (this.chartID == "electricity_production_with_ratio_potential_used_without_injection_abs"){
      unitInfo = this.helper.getUnitInfo(this.unit.energy);
    }
    else if (this.valueType === 'habitant'){
      unitInfo = this.helper.getUnitInfo('inhabitant_' + this.unit.energy);
    } else {
      unitInfo = this.helper.getUnitInfo(this.unit.energy);
    }
    unit = this.translateService.instant("UNITS." + unitInfo.translate_unit);
    this.leftYAxisLabel = name + unit;

    if (this.chartID == "electricity_production_with_ratio_potential_used_without_injection_abs"){
      // second axis : potentiel ratio used
      name = this.translateService.instant(
        "DASHBOARD.CHARTS." + this.chartID + ".RIGHT_Y_LABEL_NAME_POT_RATIO"
      );
      unitInfo = this.helper.getUnitInfo(this.unit.area_ratio);
      unit = this.translateService.instant("UNITS." + unitInfo.translate_unit);
    }
    else {
      // second axis : power axis
      name = this.translateService.instant(
        "DASHBOARD.CHARTS." + this.chartID + ".RIGHT_Y_LABEL_NAME"
      );
      unitInfo = this.helper.getUnitInfo(this.unit.power);
      unit = this.translateService.instant("UNITS." + unitInfo.translate_unit);
    }
    this.rightYAxisLabel = name + unit;
  }

  yAxisTicksCallback = value => {
    // add thousand separator where needed, and round values larger than 10
    return this.helper.getThousandSeparatorFromNumber(value);
  };

  setupOptions(): void {
    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        onClick: this.legendOnClickCallback
      },
      scales: {
        yAxes: this.yAxes,
        xAxes: [
          {
            offset: true,
            gridLines: {display: false,}
          }
        ]
      },
      tooltips: {
        callbacks: {
          label: this.tooltipsLabelCallback,
          footer: this.tooltipsFooterCallback
        }
      },
      onResize: this.resizeCallback
    };
  }

  resizeCallback = (chartInstance, newSize) => {
    // resize scatter chart line to the width of the stack
    const scatterLineDatasets = chartInstance.data.datasets.filter(
      dataset => dataset.type == "scatter" && dataset.pointStyle == "line"
    );

    scatterLineDatasets.forEach(dataset => {
      // bar width from stackoverflow : https://stackoverflow.com/questions/45707618/chart-js-get-bar-width-after-render
      const barWidth = chartInstance.getDatasetMeta(1).data[0]._model.width / 2;
      // display slightly larger line
      dataset.pointRadius = barWidth * 1.3;
      dataset.pointHoverRadius = barWidth * 1.4;
    });
  };

  /* callback has to be an anonymous function !
    elsewise "this" will not be an ElectricityProductionChartComponent
    and this.helper or this.translateService won't be available
  */
  tooltipsLabelCallback = (tooltipItem, data) => {
    let dataLabel = data.datasets[tooltipItem.datasetIndex].label + " :";
    let dataValue = tooltipItem.value;
    let unit = '';
    if (this.chartID == 'electricity_production_with_ratio_potential_used_without_injection_abs'){
      unit = data.datasets[tooltipItem.datasetIndex].unit[tooltipItem.index];
    }
    else if (this.valueType === 'habitant'){
      unit = 'inhabitant_' + data.datasets[tooltipItem.datasetIndex].unit[tooltipItem.index];
    } else {
      unit = data.datasets[tooltipItem.datasetIndex].unit[tooltipItem.index];
    }
    let unitTranslateKey = this.helper.getUnitInfo(unit).translate_unit;
    let convertedToSmaller = this.helper.convertTooBigValue(
      dataValue,
      unitTranslateKey
    );
    dataValue = convertedToSmaller[0];
    this.dataUnit = this.translateService.instant(
      "UNITS." + convertedToSmaller[1]
    );
     // apply same format as axes
     dataValue = this.helper.getThousandSeparatorFromNumber(dataValue);
    return [dataLabel, dataValue + " " + this.dataUnit];
  };

  tooltipsFooterCallback = (tooltipItem, data) => {
    const datasetIndex = tooltipItem[0].datasetIndex;
    const dataIndex = tooltipItem[0].index;
    let retval = [];

    // display only if tooltips is requested
    if (this.totalsInTooltip){
      const totals = this.chartData.datasets[datasetIndex]["totals"];
      // display only if totals provided
      if (totals) {
        let totalValue = totals[dataIndex];
        // use total unit if provided or dataset unit by default
        const datasetUnit = this.chartData.datasets[datasetIndex]["totals_unit"]
        ? this.chartData.datasets[datasetIndex]["totals_unit"]
        : this.chartData.datasets[datasetIndex]["unit"];
        const totalTranslation = this.translateService.instant(
          `DASHBOARD.CHARTS.${this.chartID}.TOTAL_TOOLTIP`
        );

        // apply same format as axes
        totalValue = this.helper.getThousandSeparatorFromNumber(totalValue);
        return [totalTranslation, totalValue + " " + this.dataUnit];
      }
    }

    return retval;
  }

  legendOnClickCallback = (e, legendItem) => {
    let originalAction = Chart.defaults.global.legend.onClick;
    originalAction.call(this, e, legendItem);
    // this.alignYAxes();
  };

  // alignYAxes() {
  //   // left axis is the main chart. Adapt right axis maximum and step size to fit left axis.
  //   const newRightAxisValues = this.helper.alignAxes(
  //     this.chart.scales.left.ticks.length,
  //     this.chart.scales.right.max
  //   );
  //   // update chart with the new maximum
  //   this.chart.options.scales.yAxes[1].ticks.maxTicksLimit = newRightAxisValues.max; // ugly hard coded value, should search through axes for the one with the correct id
  //   this.chart.options.scales.yAxes[1].ticks.stepSize = newRightAxisValues.stepSize;
  //   this.chart.update();
  // }

  resetChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }
}
